import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { LearnMore } from './learn-more';

export const SHORTCUTS: { keys: string; action: string }[] = [
  { keys: '⌘K / Ctrl K', action: 'Search everything (actors, sources, help)' },
  { keys: '?', action: 'Show keyboard shortcuts' },
  { keys: 'Esc', action: 'Close a dialog, tour or menu' },
  { keys: '↑ ↓ Enter', action: 'Move through and pick autocomplete suggestions' },
];

const typing = (t: EventTarget | null) => t instanceof HTMLElement && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));

/** Press ? anywhere (outside text fields) for keyboard shortcuts (PLAN §12.3). */
export function ShortcutsDialog() {
  const [open, setOpen] = useState(false);
  // Opened from the keyboard, not a trigger button: remember where focus was so closing puts it back (WCAG 2.4.3).
  const opener = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '?' && !typing(e.target) && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content
          onCloseAutoFocus={e => {
            if (opener.current?.isConnected && opener.current !== document.body) {
              e.preventDefault();
              opener.current.focus();
            }
          }}
          className="fixed top-1/2 left-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-surface-raised p-5 shadow-lg"
        >
          <div className="mb-3 flex items-center justify-between">
            <Dialog.Title className="font-semibold">Keyboard shortcuts</Dialog.Title>
            <Dialog.Close aria-label="Close" className="rounded p-0.5 text-muted-foreground hover:text-foreground">
              <X className="size-4" aria-hidden="true" />
            </Dialog.Close>
          </div>
          <Dialog.Description className="sr-only">Keys you can use anywhere in Hoopoe.</Dialog.Description>
          <dl className="divide-y text-sm">
            {SHORTCUTS.map(s => (
              <div key={s.keys} className="flex justify-between gap-4 py-2">
                <dt>
                  <kbd className="rounded border bg-muted px-1.5 font-mono text-xs">{s.keys}</kbd>
                </dt>
                <dd className="text-muted-foreground">{s.action}</dd>
              </div>
            ))}
          </dl>
          <LearnMore slug="accessibility" label="Keyboard, screen readers and contrast" className="mt-3" />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
